import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as React from 'react'

import { cn } from './utils'

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    orientation?: 'horizontal' | 'vertical'
    background?: 'transparent' | 'dimmed'
  }
>(
  (
    { className, orientation = 'horizontal', background = 'dimmed', ...props },
    ref
  ) => (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'flex rounded-full gap-1 p-1',
        background === 'dimmed' ? 'bg-gray-100/50' : 'transparent',
        orientation === 'vertical' ? 'flex-col h-auto w-full' : '',
        className
      )}
      {...props}
    />
  )
)
TabsList.displayName = TabsPrimitive.List.displayName

const tabSizes = {
  sm: 'px-2 py-1 text-xs h-7',
  md: 'px-3 py-1.5 text-xs h-8',
  lg: 'px-4 py-2 text-base',
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    orientation?: 'horizontal' | 'vertical'
    size?: 'sm' | 'md' | 'lg'
    subtle?: boolean
  }
>(
  (
    {
      className,
      orientation = 'horizontal',
      size = 'md',
      subtle = false,
      ...props
    },
    ref
  ) => (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'inline-flex items-center text-xs justify-center whitespace-nowrap rounded-full ring-offset-background transition-all disabled:pointer-events-none disabled:opacity-50 border',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        subtle ? 'button-ghost' : 'button-secondary',
        'data-[state=active]:shadow-sm',
        tabSizes[size],
        orientation === 'vertical' ? 'w-full justify-start' : '',

        className
      )}
      {...props}
    />
  )
)
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
